import React, { useEffect, useState } from "react";
import { Box } from "@mantine/core";
import { motion } from "framer-motion";
import { PlayerType, ThemeType } from "../util/types";
import DynamicText from "./DynamicText";
import { DEFAULT_THEME } from "../util/consts";
import PlayerReminder from "./PlayerReminder";

const DURATION_SEC = 1.5;
const DELAY_SEC = 0.3;

type ShowScoresProps = {
  players: PlayerType[];
  round?: string;
  reminder?: string;
  instructions?: string;
  theme?: ThemeType;
  onRenderComplete?: () => Promise<void>;
};

const ShowScores = ({
  players,
  round,
  reminder,
  instructions,
  theme = DEFAULT_THEME,
  onRenderComplete = async () => {},
}: ShowScoresProps) => {
  const [orderedScores, setOrderedScores] = useState<PlayerType[]>([]);
  useEffect(() => {
    setOrderedScores(players.sort((a, b) => (b.Score || 0) - (a.Score || 0)));
  }, [players]);
  const animationTime = players.length * DURATION_SEC + DELAY_SEC;
  useEffect(() => {
    const animationComplete = async () => {
      await new Promise((resolve) => setTimeout(resolve, animationTime * 1000));
      await onRenderComplete();
    };
    animationComplete();
  }, [onRenderComplete, animationTime]);
  const fontSize = 35 - orderedScores.length * 2;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignContent: "center",
      }}
    >
      {round && (
        <DynamicText
          style={{
            color: theme.ButtonColor,
            fontSize: 15,
            marginTop: 10,
          }}
        >
          {round}
        </DynamicText>
      )}
      {instructions && (
        <DynamicText
          style={{
            fontSize: 30,
          }}
        >
          {instructions}
        </DynamicText>
      )}
      {orderedScores.map((p, i) => {
        return (
          <motion.div
            key={p.Score + p.Name + i}
            initial={{ scale: 0, opacity: 0, y: 200 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{
              delay: DELAY_SEC * (i + 1),
              duration: DURATION_SEC,
              ease: ["easeInOut"],
            }}
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                border: "2px solid white",
                borderRadius: 20,
                marginBottom: 110 - orderedScores.length * 10,
                padding: 10,
                marginRight: 10,
                marginLeft: 10,
              }}
            >
              <DynamicText
                style={{
                  fontSize: fontSize,
                  justifyContent: "left",
                  width: "50%",
                  marginLeft: 20,
                }}
              >
                {p.Name}
              </DynamicText>
              <DynamicText
                style={{
                  fontSize: fontSize,
                  width: "150%",
                  justifyContent: "center",
                }}
              >
                {p.Caption ?? ""}
              </DynamicText>
              <DynamicText
                style={{
                  fontSize: fontSize,
                  textAlign: "right",
                  width: "50%",
                  justifyContent: "center",
                }}
              >
                {p.Score}
              </DynamicText>
            </Box>
          </motion.div>
        );
      })}
      {reminder && <PlayerReminder reminder={reminder} theme={theme} />}
    </Box>
  );
};

export default ShowScores;
